/** Conteúdo de páginas institucionais com layout rico (pagina_institucional.v1). */

export const INSTITUTIONAL_VARIANT_BY_TIPO = Object.freeze({
  sobre: "sobre",
  informacoes: "informacoes",
  beneficios: "beneficios",
  sustentabilidade: "sustentabilidade",
  vale_transporte: "vale_transporte",
  trabalhe_conosco: "trabalhe_conosco",
  formas_pagamento: "formas_pagamento",
  encomendas: "encomendas",
  como_comprar: "como_comprar",
  elas: "elas",
});

/** informacoes/acessibilidade usa variant dedicada */
export const INSTITUTIONAL_SLUG_VARIANTS = Object.freeze({
  "informacoes:acessibilidade": "acessibilidade",
});

const str = (v) => String(v ?? "").trim();

const strList = (v) => (Array.isArray(v) ? v.map((x) => str(x)).filter(Boolean) : []);

const linkList = (v, opts = {}) =>
  Array.isArray(v)
    ? v
        .map((x) => {
          const label = str(x?.label);
          const href = str(x?.href);
          if (!label || !href) return null;
          const item = { label, href };
          if (opts.external) item.external = !!x?.external;
          return item;
        })
        .filter(Boolean)
    : [];

const faqItems = (v) =>
  Array.isArray(v)
    ? v
        .map((x) => ({
          question: str(x?.question),
          answer: str(x?.answer),
        }))
        .filter((x) => x.question && x.answer)
    : [];

const blocksList = (v) =>
  Array.isArray(v)
    ? v
        .map((b) => {
          const title = str(b?.title);
          if (!title) return null;
          return { title, items: strList(b?.items) };
        })
        .filter(Boolean)
    : [];

const metricsList = (v) =>
  Array.isArray(v)
    ? v
        .map((m) => {
          const label = str(m?.label);
          if (!label) return null;
          return { label, primary: str(m?.primary), secondary: str(m?.secondary) };
        })
        .filter(Boolean)
    : [];

const checksList = (v) =>
  Array.isArray(v)
    ? v
        .map((c) => {
          const title = str(c?.title);
          if (!title) return null;
          return { title, body: str(c?.body) };
        })
        .filter(Boolean)
    : [];

const helpChannelsList = (v) =>
  Array.isArray(v)
    ? v
        .map((c) => {
          const label = str(c?.label);
          if (!label) return null;
          return {
            type: str(c?.type) || undefined,
            label,
            value: str(c?.value) || undefined,
            href: str(c?.href) || undefined,
            fallbackLabel: str(c?.fallbackLabel) || undefined,
            fallbackValue: str(c?.fallbackValue) || undefined,
            fallbackHref: str(c?.fallbackHref) || undefined,
          };
        })
        .filter(Boolean)
    : [];

const stepsList = (v) =>
  Array.isArray(v)
    ? v.map((s) => {
        if (typeof s === "string") return str(s);
        const title = str(s?.title);
        if (!title) return null;
        return { title, body: str(s?.body) };
      }).filter(Boolean)
    : [];

export function institutionalVariantForTipo(tipo, slug) {
  const t = String(tipo || "").trim();
  const s = String(slug || "").trim();
  const key = `${t}:${s}`;
  if (INSTITUTIONAL_SLUG_VARIANTS[key]) return INSTITUTIONAL_SLUG_VARIANTS[key];
  return INSTITUTIONAL_VARIANT_BY_TIPO[t] || null;
}

export function emptyInstitutionalPage(variant) {
  return sanitizeInstitutionalPage(
    {
      format: "pagina_institucional.v1",
      variant: variant || "informacoes",
      meta: { title: "", description: "" },
      backButtonLabel: "Voltar",
      labels: {},
      hero: { ctas: [], chips: [], highlights: [], sidebarSteps: [] },
      cards: [],
      sections: [],
      textSections: [],
      faq: { title: "", items: [] },
      closingCta: { ctas: [], links: [], channels: [] },
    },
    variant
  );
}

function sanitizeObjBlock(o, fields) {
  if (!o || typeof o !== "object") return undefined;
  const out = {};
  let has = false;
  for (const [k, fn] of Object.entries(fields)) {
    const val = fn(o[k], o);
    if (val !== undefined && val !== "" && !(Array.isArray(val) && val.length === 0)) {
      out[k] = val;
      has = true;
    }
  }
  return has ? out : undefined;
}

export function sanitizeInstitutionalPage(src, variantHint) {
  const v = src && typeof src === "object" ? src : {};
  const variant = str(v.variant) || variantHint || "informacoes";
  const heroIn = v.hero && typeof v.hero === "object" ? v.hero : {};
  const metaIn = v.meta && typeof v.meta === "object" ? v.meta : {};
  const faqIn = v.faq && typeof v.faq === "object" ? v.faq : {};
  const closingIn = v.closingCta && typeof v.closingCta === "object" ? v.closingCta : {};
  const labelsIn = v.labels && typeof v.labels === "object" ? v.labels : {};

  const cards = Array.isArray(v.cards)
    ? v.cards
        .map((c) => {
          const o = c && typeof c === "object" ? c : {};
          const title = str(o.title);
          if (!title) return null;
          return {
            id: str(o.id) || undefined,
            icon: str(o.icon) || undefined,
            title,
            summary: str(o.summary) || undefined,
            desc: str(o.desc) || undefined,
            description: str(o.description) || undefined,
            bullets: strList(o.bullets),
          };
        })
        .filter(Boolean)
    : [];

  const sections = Array.isArray(v.sections)
    ? v.sections
        .map((s) => {
          const o = s && typeof s === "object" ? s : {};
          const title = str(o.title);
          const id = str(o.id);
          if (!title || !id) return null;
          return {
            id,
            title,
            eyebrow: str(o.eyebrow) || undefined,
            summary: str(o.summary) || undefined,
            highlights: strList(o.highlights),
            warning: str(o.warning) || undefined,
            blocks: blocksList(o.blocks),
            links: linkList(o.links, { external: true }),
          };
        })
        .filter(Boolean)
    : [];

  const textSections = Array.isArray(v.textSections)
    ? v.textSections
        .map((s) => {
          const o = s && typeof s === "object" ? s : {};
          if (!str(o.title) && !str(o.body) && !str(o.description) && !strList(o.paragraphs).length) return null;
          return {
            id: str(o.id) || undefined,
            eyebrow: str(o.eyebrow) || undefined,
            icon: str(o.icon) || undefined,
            title: str(o.title) || undefined,
            description: str(o.description) || undefined,
            body: str(o.body) || undefined,
            paragraphs: strList(o.paragraphs),
            items: strList(o.items),
            ctaLabel: str(o.ctaLabel) || undefined,
            ctaHref: str(o.ctaHref) || undefined,
            note: str(o.note) || undefined,
            noteIcon: str(o.noteIcon) || undefined,
            links: linkList(o.links, { external: true }),
            cards: Array.isArray(o.cards)
              ? o.cards
                  .map((c) => {
                    const title = str(c?.title);
                    if (!title) return null;
                    return { title, body: str(c?.body) };
                  })
                  .filter(Boolean)
              : undefined,
          };
        })
        .filter(Boolean)
    : [];

  const purpose = sanitizeObjBlock(v.purpose, {
    eyebrow: str,
    title: str,
    paragraphs: strList,
    sidebarEyebrow: str,
    sidebarTitle: str,
  });

  const history = sanitizeObjBlock(v.history, {
    eyebrow: str,
    title: str,
    intro: str,
    milestones: (arr) =>
      Array.isArray(arr)
        ? arr
            .map((m) => {
              const title = str(m?.title);
              if (!title) return null;
              return { label: str(m?.label) || undefined, title, desc: str(m?.desc) };
            })
            .filter(Boolean)
        : [],
  });

  const video = sanitizeObjBlock(v.video, {
    badge: str,
    title: str,
    description: str,
    url: str,
    poster: str,
    note: str,
  });

  const family = sanitizeObjBlock(v.family, {
    eyebrow: str,
    title: str,
    image: str,
    imageAlt: str,
    paragraphs: strList,
  });

  const archive = sanitizeObjBlock(v.archive, {
    eyebrow: str,
    title: str,
    image: str,
    imageAlt: str,
    paragraphs: strList,
    ctas: (x) => linkList(x),
  });

  const headquarters = sanitizeObjBlock(v.headquarters, {
    eyebrow: str,
    title: str,
    companyName: str,
    addressLines: strList,
    phone: str,
    phoneLabel: str,
    ctaLabel: str,
    ctaHref: str,
    citiesEyebrow: str,
    cities: strList,
    cnpj: str,
    schemaPhone: str,
  });

  const map = sanitizeObjBlock(v.map, {
    title: str,
    subtitle: str,
    badge: str,
    query: str,
  });

  const banner = sanitizeObjBlock(v.banner, {
    image: str,
    imageAlt: str,
  });

  const areasSection = sanitizeObjBlock(v.areasSection, {
    eyebrow: str,
    title: str,
  });

  const pillars = Array.isArray(v.pillars)
    ? v.pillars
        .map((p) => {
          const title = str(p?.title);
          if (!title) return null;
          return { icon: str(p?.icon) || undefined, title, description: str(p?.description) };
        })
        .filter(Boolean)
    : undefined;

  const impactNumbers = Array.isArray(v.impactNumbers)
    ? v.impactNumbers
        .map((n) => {
          const value = str(n?.value);
          const label = str(n?.label);
          if (!value || !label) return null;
          return { value, label, detail: str(n?.detail) };
        })
        .filter(Boolean)
    : undefined;

  const areas = Array.isArray(v.areas)
    ? v.areas
        .map((a) => {
          const title = str(a?.title);
          if (!title) return null;
          return { icon: str(a?.icon) || undefined, title, description: str(a?.description) };
        })
        .filter(Boolean)
    : undefined;

  const formIntro =
    v.formIntro && typeof v.formIntro === "object"
      ? JSON.parse(JSON.stringify(v.formIntro))
      : undefined;

  const cityFinderIn = v.cityFinder && typeof v.cityFinder === "object" ? v.cityFinder : {};
  const cityFinder = {
    title: str(cityFinderIn.title) || undefined,
    body: str(cityFinderIn.body) || undefined,
    queryLabel: str(cityFinderIn.queryLabel) || undefined,
    queryPlaceholder: str(cityFinderIn.queryPlaceholder) || undefined,
    stateLabel: str(cityFinderIn.stateLabel) || undefined,
    allStatesLabel: str(cityFinderIn.allStatesLabel) || undefined,
    emptyList: str(cityFinderIn.emptyList) || undefined,
    noMatch: str(cityFinderIn.noMatch) || undefined,
    whatsappCta: str(cityFinderIn.whatsappCta) || undefined,
  };

  const closingCta = {
    eyebrow: str(closingIn.eyebrow) || undefined,
    title: str(closingIn.title) || undefined,
    description: str(closingIn.description) || undefined,
    note: str(closingIn.note) || undefined,
    ctas: linkList(closingIn.ctas),
    links: linkList(closingIn.links, { external: true }),
    channels: helpChannelsList(closingIn.channels),
  };

  const labels = {
    cardDetailLink: str(labelsIn.cardDetailLink) || undefined,
    sectionHighlightsTitle: str(labelsIn.sectionHighlightsTitle) || undefined,
    sectionLinksTitle: str(labelsIn.sectionLinksTitle) || undefined,
    detailOpen: str(labelsIn.detailOpen) || undefined,
    detailClose: str(labelsIn.detailClose) || undefined,
  };

  return {
    format: "pagina_institucional.v1",
    variant,
    meta: {
      title: str(metaIn.title),
      description: str(metaIn.description),
      openGraphDescription: str(metaIn.openGraphDescription) || undefined,
    },
    backButtonLabel: str(v.backButtonLabel) || "Voltar",
    labels,
    hero: {
      eyebrow: str(heroIn.eyebrow),
      title: str(heroIn.title),
      subtitle: str(heroIn.subtitle),
      note: str(heroIn.note) || undefined,
      image: str(heroIn.image) || undefined,
      imageAlt: str(heroIn.imageAlt) || undefined,
      ctas: linkList(heroIn.ctas),
      chips: linkList(heroIn.chips),
      highlights: strList(heroIn.highlights),
      sidebarTitle: str(heroIn.sidebarTitle) || undefined,
      sidebarNote: str(heroIn.sidebarNote) || undefined,
      sidebarSteps: strList(heroIn.sidebarSteps),
    },
    cards,
    sections,
    textSections,
    metrics: metricsList(v.metrics),
    prohibitedItems: strList(v.prohibitedItems),
    checks: checksList(v.checks),
    steps: stepsList(v.steps),
    highlights: strList(v.highlights),
    employeeSteps: checksList(v.employeeSteps),
    pillars: pillars?.length ? pillars : undefined,
    impactNumbers: impactNumbers?.length ? impactNumbers : undefined,
    purpose,
    strengths: strList(v.strengths),
    history,
    video,
    family,
    archive,
    headquarters,
    map,
    banner,
    areasSection,
    areas: areas?.length ? areas : undefined,
    formIntro,
    helpChannels: helpChannelsList(v.helpChannels),
    footerNote: str(v.footerNote) || undefined,
    faq: {
      title: str(faqIn.title) || undefined,
      items: faqItems(faqIn.items),
    },
    cityFinder,
    closingCta,
  };
}

export function parseInstitutionalPageFromContent(contentData, tipo, slug) {
  const variant = institutionalVariantForTipo(tipo, slug || contentData?.slug);
  if (!variant) return emptyInstitutionalPage("informacoes");

  let raw = contentData?.conteudo_json;
  if (typeof raw === "string" && raw.trim()) {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }

  if (raw?.format === "pagina_institucional.v1") {
    return sanitizeInstitutionalPage(raw, variant);
  }

  return emptyInstitutionalPage(variant);
}
